import React, { useState } from "react";

import Select from "../../../../components/input/select/Select";
import TextField from "../../../../components/input/textfield/TextField";
import {
  FormSection,
  FormRow,
} from "../../../../components/layout/forms/Index";

import { sportsDataset } from "../../../../assets/dataset/SportsDataset";

import TalentModal from "./TalentModal";
import { clean, getApiError } from "../utils/talentUtils";

import "./TalentForms.css";

const roleOptions = ["Player", "Coach", "Player / Coach", "Athlete"].map(
  (value) => ({ label: value, value }),
);

const availabilityOptions = [
  "Available",
  "Unavailable",
  "Limited",
  "Retired",
].map((value) => ({ label: value, value }));

const initialFields = (initial) => ({
  sport: initial?.sport || "",
  role: initial?.role || "Player",
  mainPosition: initial?.mainPosition || "",
  otherPosition: initial?.otherPosition || "",
  availability: initial?.availability || "Available",
});

export default function TalentSportForm({
  initial,
  saving = false,
  onSave,
  onClose,
}) {
  const [fields, setFields] = useState(() => initialFields(initial));
  const [errors, setErrors] = useState({});
  const [error, setError] = useState("");

  const change = ({ target }) => {
    const { name, value } = target;

    setFields((previous) => ({
      ...previous,
      [name]: value,
      ...(["sport", "role"].includes(name) && previous[name] !== value
        ? { mainPosition: "", otherPosition: "" }
        : {}),
    }));

    setErrors((previous) => ({
      ...previous,
      [name]: "",
      ...(["sport", "role"].includes(name)
        ? { mainPosition: "", otherPosition: "" }
        : {}),
    }));

    setError("");
  };

  const save = async () => {
    if (saving) return;

    const next = {};
    const mainPosition = clean(fields.mainPosition);
    const otherPosition = clean(fields.otherPosition);

    if (!sportsDataset.some(({ value }) => value === fields.sport)) {
      next.sport = "Select a valid sport.";
    }

    if (!roleOptions.some(({ value }) => value === fields.role)) {
      next.role = "Select a valid role.";
    }

    if (
      mainPosition &&
      otherPosition &&
      mainPosition.toLowerCase() === otherPosition.toLowerCase()
    ) {
      next.otherPosition = "Positions must be different.";
    }

    if (
      !availabilityOptions.some(({ value }) => value === fields.availability)
    ) {
      next.availability = "Select valid availability.";
    }

    setErrors(next);

    if (Object.keys(next).length) return;

    try {
      setError("");

      await onSave({
        sport: fields.sport,
        role: fields.role,
        mainPosition: mainPosition || null,
        otherPosition: otherPosition || null,
        availability: fields.availability,
      });
    } catch (err) {
      setError(getApiError(err));
    }
  };

  return (
    <TalentModal
      title={initial ? "Edit Sport" : "Add Sport"}
      description={
        initial
          ? "Update your sport, role or positions."
          : "Add a sport and your current role or positions."
      }
      busy={saving}
      onClose={onClose}
      onSave={save}
    >
      {error && (
        <p className="user-talent__error" role="alert">
          {error}
        </p>
      )}

      <FormSection>
        <FormRow>
          <Select
            label="Sport"
            name="sport"
            value={fields.sport}
            items={sportsDataset}
            onChange={change}
            error={errors.sport}
            defaultOption="Select sport"
            required
          />

          <Select
            label="Role"
            name="role"
            value={fields.role}
            items={roleOptions}
            onChange={change}
            error={errors.role}
            required
          />

          <TextField
            label="Main Position"
            name="mainPosition"
            value={fields.mainPosition}
            onChange={change}
            placeholder="e.g. Outside Centre"
            maxLength={100}
          />

          <TextField
            label="Other Position"
            name="otherPosition"
            value={fields.otherPosition}
            onChange={change}
            placeholder="Optional"
            maxLength={100}
          />

          <Select
            label="Availability"
            name="availability"
            value={fields.availability}
            items={availabilityOptions}
            onChange={change}
            error={errors.availability}
            required
          />
        </FormRow>
      </FormSection>
    </TalentModal>
  );
}
